import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Le tabelle del passo 4 della scheda di verifica (limiti di spesa, requisiti di accesso, categorie del bando:
// LimitiSpesa, RequisitiAccesso e CondizioniCategorie di pages/VerificaBandi.js): quello che React ricalcolava a ogni
// modifica di una riga. Sta sullo stesso elemento di righe_controller (che aggiunge e toglie le righe).
// - Visibilità: un elemento con data-se='{"tipo":["a","b"],"base":["categorie"]}' si vede quando ogni campo della sua
//   riga (il campo il cui name finisce con «[tipo]», una casella come "true"/"false") ha uno dei valori; "*" vuol dire
//   non vuoto, "!v" diverso da v. Vale anche per le voci di un menu (i confronti ammessi dal tipo del limite).
// - Al cambio del tipo, un campo con data-al-cambio-tipo='{"progetto_min":"almeno","*":"al_massimo"}' prende il valore
//   del nuovo tipo (set(i, {tipo, confronto}) dell'originale) e il suo menu si riallinea.
// - Un campo con data-attiva-presente, cambiato, attiva il campo data-presente della riga (la chiave che React aveva
//   solo dopo la prima modifica, es. «rapporti»).
// - data-incompleti='{doc_id: motivo}': l'avviso «testo incompleto» dell'atto scelto nella riga.
// - L'esito di «Controlla» ([data-esito]) si svuota quando la riga cambia (esiti[i] = null) e tutti gli esiti quando
//   si toglie una riga (setEsiti({})).
// - Il riassunto del riquadro («2 limiti», «1 riga») e il testo «nessuna riga» ([data-vuoto]) seguono le righe.
// - «Controlla» (data-url, name = dati[tabella][i][_controlla]) e «Proponi dagli atti» mandano i campi del form del
//   passo con fetch, come le chiamate api.post di React: ogni clic per conto suo, il form non diventa «occupato» e
//   «Conferma i dati» non gira. La risposta è un turbo-stream (l'esito della riga, le proposte); senza una risposta
//   leggibile l'esito di ripiego del <template> «nonRiuscito» («Controllo non riuscito» / «Proposta non riuscita»).
//   Durante «Proponi» il pulsante è disattivato e gira la sua icona (busyP).
export default class extends Controller {
  static targets = ["nonRiuscito", "propostaNonRiuscita", "esitoProposta", "attesa"]
  static values = { uno: String, molti: String }

  async controlla(event) {
    const pulsante = event.currentTarget
    const riga = pulsante.closest("[data-righe-target~='riga']")
    try {
      await this.invia(pulsante)
    } catch {
      const esito = riga?.querySelector("[data-esito]")
      if (esito && this.hasNonRiuscitoTarget) esito.innerHTML = this.nonRiuscitoTarget.innerHTML
    }
  }

  async proponi(event) {
    const pulsante = event.currentTarget
    pulsante.disabled = true
    this.attesaTargets.forEach((el) => { el.hidden = false })
    try {
      await this.invia(pulsante)
    } catch {
      if (this.hasEsitoPropostaTarget && this.hasPropostaNonRiuscitaTarget) this.esitoPropostaTarget.innerHTML = this.propostaNonRiuscitaTarget.innerHTML
    } finally {
      pulsante.disabled = false
      this.attesaTargets.forEach((el) => { el.hidden = true })
    }
  }

  async invia(pulsante) {
    const dati = new FormData(this.element.closest("form"))
    if (pulsante.name) dati.append(pulsante.name, "1")
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const risposta = await fetch(pulsante.dataset.url, {
      method: "POST", body: dati,
      headers: { Accept: "text/vnd.turbo-stream.html", ...(token ? { "X-CSRF-Token": token } : {}) },
    })
    // una risposta rediretta (sessione scaduta) o senza turbo-stream non è un esito
    if (risposta.redirected || !(risposta.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("risposta")
    Turbo.renderStreamMessage(await risposta.text())
  }

  connect() {
    this.righe().forEach((riga) => this.aggiornaRiga(riga))
    this.conta()
  }

  righe() {
    return [...this.element.querySelectorAll("[data-righe-target~='riga']")]
  }

  cambio(event) {
    const riga = event.target.closest?.("[data-righe-target~='riga']")
    if (!riga || !this.element.contains(riga)) return
    if (event.type === "change" && /\[tipo\]$/.test(event.target.name || "")) this.nuovoTipo(riga, event.target.value)
    if (event.type === "change" && event.target.dataset.attivaPresente !== undefined) {
      riga.querySelectorAll("[data-presente]").forEach((el) => el.removeAttribute("disabled"))
    }
    riga.querySelectorAll("[data-esito]").forEach((el) => { el.innerHTML = "" })
    this.aggiornaRiga(riga)
  }

  aggiunta(event) {
    const riga = event.detail?.riga
    if (riga) this.aggiornaRiga(riga)
    this.conta()
  }

  tolta() {
    this.element.querySelectorAll("[data-esito]").forEach((el) => { el.innerHTML = "" })
    this.conta()
  }

  // turbo:morph-element@document: il riquadro intorno può essere stato riscritto con il conteggio salvato
  riletto() {
    this.conta()
  }

  valore(riga, campo) {
    const casella = riga.querySelector(`input[type='checkbox'][name$='[${campo}]']`)
    if (casella) return String(casella.checked)
    return riga.querySelector(`[name$='[${campo}]']`)?.value ?? ""
  }

  vale(v, valori) {
    if (valori.includes("*")) return v !== ""
    if (valori.length && valori.every((x) => x.startsWith("!"))) return !valori.includes(`!${v}`)
    return valori.includes(v)
  }

  aggiornaRiga(riga) {
    riga.querySelectorAll("[data-se]").forEach((el) => {
      const cond = JSON.parse(el.dataset.se)
      el.hidden = !Object.entries(cond).every(([campo, valori]) => this.vale(this.valore(riga, campo), valori))
    })
    riga.querySelectorAll("[data-incompleti]").forEach((el) => {
      const motivo = JSON.parse(el.dataset.incompleti)[this.valore(riga, "doc_id")]
      el.hidden = !motivo
      const testo = el.querySelector("[data-incompleto-testo]")
      if (testo) testo.textContent = motivo || ""
    })
  }

  nuovoTipo(riga, tipo) {
    riga.querySelectorAll("[data-al-cambio-tipo]").forEach((campo) => {
      const mappa = JSON.parse(campo.dataset.alCambioTipo)
      campo.value = mappa[tipo] ?? mappa["*"]
      const menu = campo.closest("[data-controller~='select']")
      this.application.getControllerForElementAndIdentifier(menu, "select")?.sync()
    })
  }

  conta() {
    const n = this.righe().length
    this.element.querySelectorAll("[data-vuoto]").forEach((el) => { el.hidden = n > 0 })
    const riassunto = this.element.closest("[data-controller~='riquadro']")?.querySelector("[data-testid$='-riassunto']")
    if (riassunto && this.hasUnoValue) riassunto.textContent = `${n} ${n === 1 ? this.unoValue : this.moltiValue}`
  }
}
